import { formatAbn } from "utils/abn";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import RctiEligibility from "components/RctiEligibility";
import { verifyAbrAbn } from "utils/abrAPI";
import { agreementRequest } from "utils/rctiAgreementAPI";
import "layouts/RCTI/agreements.css";
export default function BusinessEligibilityProfile({ business, onSaved }) {
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false),
    [verified, setVerified] = useState(null),
    [error, setError] = useState(""),
    [message, setMessage] = useState("");
  const requestID = useRef(0);
  const savedAbn = String(business.abnNumber || "").trim();
  useEffect(() => {
    setValues({
      abnNumber: business.abnNumber || "",
      gstRegistered: business.gstRegistered ?? null,
      isLargeBusiness: business.isLargeBusiness ?? null,
      isGovernmentEntity: business.isGovernmentEntity ?? null,
    });
  }, [
    business.abnNumber,
    business.gstRegistered,
    business.isLargeBusiness,
    business.isGovernmentEntity,
  ]);
  useEffect(
    () => () => {
      requestID.current++;
    },
    []
  );
  const verify = async () => {
    const request = ++requestID.current;
    const result = await verifyAbrAbn(String(values.abnNumber || "").trim());
    if (request !== requestID.current)
      throw new Error("The ABN changed. Please verify it again.");
    if (!result.active) throw new Error("This ABN is not active on ABR.");
    setVerified(result);
    return result;
  };
  const run = async (action) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="rcti-profile-eligibility">
      <h2>Your business eligibility</h2>
      <p>
        Update your RCTI eligibility here. These settings remain editable after
        saving.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          run(async () => {
            const abn = String(values.abnNumber || "").replace(/[\s-]/g, "");
            if (!savedAbn && abn && verified?.abn !== abn) await verify();
            const patch = await agreementRequest(
              "/settings",
              { ...values, abnNumber: savedAbn || abn },
              "put"
            );
            onSaved(patch);
            setMessage("Business eligibility saved.");
          });
        }}
      >
        <label>
          Business ABN
          <input
            inputMode="numeric"
            maxLength={14}
            readOnly={Boolean(savedAbn)}
            disabled={busy}
            value={formatAbn(values.abnNumber)}
            onChange={(event) => {
              const abnNumber = event.target.value.replace(/\s/g, "").replace(/[^\d]/g, "");
              requestID.current++;
              setVerified(null);
              setError("");
              setValues((current) => ({ ...current, abnNumber }));
            }}
            aria-describedby="profile-eligibility-abn-help"
          />
        </label>
        <p id="profile-eligibility-abn-help">
          {savedAbn
            ? "Your ABN is saved and cannot be edited."
            : "Add your 11-digit ABN. ABR must confirm an active registration before saving. Once saved, it cannot be changed or removed."}
        </p>
        {!savedAbn && (
          <button
            type="button"
            className="profile-abr-check"
            disabled={busy || !values.abnNumber}
            onClick={() => run(verify)}
          >
            {busy ? "Checking…" : "Verify ABN with ABR"}
          </button>
        )}
        {verified && (
          <p className="profile-abr-success" role="status">
            Verified: {verified.companyName} · Active ABN
          </p>
        )}
        <RctiEligibility
          values={values}
          disabled={busy}
          onChange={(key, value) =>
            setValues((current) => ({ ...current, [key]: value }))
          }
        />
        {error && (
          <p role="alert" className="profile-abr-error">
            {error}
          </p>
        )}
        {message && <p role="status">{message}</p>}
        <div className="rcti-agreements__actions">
          <button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save business eligibility"}
          </button>
          <Link to="/rcti-agreements">RCTI Agreements</Link>
        </div>
      </form>
    </section>
  );
}
BusinessEligibilityProfile.propTypes = {
  business: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
};
